/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React from 'react';
import { Provider } from 'react-redux';

import '@algolia/autocomplete-theme-classic';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled, useTheme } from '@mui/material/styles';
import { AutocompleteSource, AutocompleteState } from '@algolia/autocomplete-js';
import { OnSubmitParams, GetSourcesParams, OnSelectParams } from '@algolia/autocomplete-core';
import type { Render, VNode } from '@algolia/autocomplete-js';
import './AlgoliaAutocomplete.scss';
import { Hit } from '@algolia/client-search';

import { AlgoliaQueryHitItem, fetchAlgoliaAutocompleteResults } from './Algolia_network';
import { AudienceType } from '../algorithm/types';
import { IconsForDocumentFlags } from '../documentVersionFlags/documentVersionFlags_utils';
import { store } from '../../../app/store';
import { AutocompleteComponent } from './AutocompleteComponent';
import { useDebounce } from '../utils/customHooks';

export interface AutocompleteRendererProps {
  onEnter: (input: string, slug: string | undefined) => Promise<void>;
  redirectToChat?: () => void;
  audience: AudienceType;
  publishedOnly: boolean;
  className?: string;
}

export const AutocompletePanel = (item: { item: AlgoliaQueryHitItem }) => {
  return (
    <div>
      <span className="suggestion-text">
        <Provider store={store}>
          <IconsForDocumentFlags flags={item.item.flags} fontSize="small" style={{ marginTop: -2 }} />
          <span>{item.item.title}</span>
        </Provider>
      </span>
    </div>
  );
};

type TStyledPanel = {
  calcHeight: number;
};

export const StyledPanel = styled('div', {
  // prevent leaking custom props to the DOM
  shouldForwardProp: (prop) => prop !== 'calcHeight',
})<TStyledPanel>(({ calcHeight }) => ({
  height: calcHeight,
  maxHeight: calcHeight,
}));

export const getHeight = (itemCount: number, isLargeScreen: boolean) => {
  const itemSize = isLargeScreen ? 36 : 48;
  if (itemCount > 8) {
    return 8 * itemSize;
  }
  return itemCount * itemSize;
};

const AutocompleteRenderer: React.FC<AutocompleteRendererProps> = ({
  onEnter,
  redirectToChat,
  audience,
  publishedOnly,
  className,
}) => {
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });

  const debouncedSearchClient = useDebounce(fetchAlgoliaAutocompleteResults, 300);

  const onSubmitHandler = ({ state, event }: OnSubmitParams<AlgoliaQueryHitItem>) => {
    if (event.type === 'submit') {
      state.isOpen = false;
      void onEnter(state.query, undefined /* no slug */);
      redirectToChat && redirectToChat();
    }
  };

  return (
    <AutocompleteComponent
      getSources={async (query: GetSourcesParams<AlgoliaQueryHitItem>) => {
        const items: Array<Hit<AlgoliaQueryHitItem>> = await debouncedSearchClient(
          query.query,
          publishedOnly,
          audience
        );

        const sources: AutocompleteSource<AlgoliaQueryHitItem>[] = [
          {
            sourceId: 'querySuggestions',
            getItems() {
              return items;
            },
            getItemInputValue() {
              return '';
            },
            onSelect(item: OnSelectParams<AlgoliaQueryHitItem>) {
              return (
                <>
                  {onEnter(item.item.title, item.item.doc_slug)}
                  {redirectToChat && redirectToChat()}
                </>
              );
            },

            templates: {
              item: AutocompletePanel,
            },
          },
        ];
        return sources;
      }}
      customPanelRender={(
        { children, render, state }: { children: VNode; render: Render; state: AutocompleteState<AlgoliaQueryHitItem> },
        root: HTMLElement
      ) => {
        render(
          <StyledPanel
            data-testid="autocomplete-panel"
            calcHeight={getHeight(state?.collections?.[0]?.items?.length ?? 0, smUp)}
          >
            {children}
          </StyledPanel>,
          root
        );
      }}
      onSubmit={onSubmitHandler}
      className={className}
    />
  );
};

export default AutocompleteRenderer;
